import {toast} from 'react-toastify';
import {toastConstants} from '../constants';
import React from 'react';
import {API} from './api';
import { normalizeMemberGroup, normalizeScopeTier } from './chatLink';
import i18n from '../i18n';

const HTMLToastContent = ({ htmlContent }) => {
  return <div dangerouslySetInnerHTML={{ __html: htmlContent }} />;
};
export default HTMLToastContent;

export function isAdmin() {
  let user = localStorage.getItem('user');
  if (!user) return false;
  user = JSON.parse(user);
  return user.role >= 10;
}

export function isRoot() {
  let user = localStorage.getItem('user');
  if (!user) return false;
  user = JSON.parse(user);
  return user.role >= 100;
}

export function getSystemName() {
  let system_name = localStorage.getItem('system_name');
  if (!system_name) return 'UPLINK';
  return system_name;
}

export function getLogo() {
  let logo = localStorage.getItem('logo');
  if (!logo) return '/logo.png';
  return logo;
}

export function getFooterHTML() {
  return localStorage.getItem('footer_html');
}

export async function copy(text) {
  let okay = true;
  try {
    await navigator.clipboard.writeText(text);
  } catch (e) {
    okay = false;
    console.error(e);
  }
  return okay;
}

export function isMobile() {
  return window.innerWidth <= 600;
}

let showErrorOptions = { autoClose: toastConstants.ERROR_TIMEOUT };
let showWarningOptions = { autoClose: toastConstants.WARNING_TIMEOUT };
let showSuccessOptions = { autoClose: toastConstants.SUCCESS_TIMEOUT };
let showInfoOptions = { autoClose: toastConstants.INFO_TIMEOUT };
let showNoticeOptions = { autoClose: false };

if (isMobile()) {
  showErrorOptions.position = 'top-center';
  // showErrorOptions.transition = 'flip';

  showSuccessOptions.position = 'top-center';
  // showSuccessOptions.transition = 'flip';

  showInfoOptions.position = 'top-center';
  // showInfoOptions.transition = 'flip';

  showNoticeOptions.position = 'top-center';
  // showNoticeOptions.transition = 'flip';
}

export function showError(error) {
  if (!error) return;
  console.error(error);
  if (error.message) {
    if (error.name === 'AxiosError') {
      if (!error.response) {
        toast.error(i18n.t('messages.error.network_unreachable'), showErrorOptions);
        return;
      }
      switch (error.response.status) {
        case 401:
          window.location.href = '/login?expired=true';
          break;
        case 429:
          toast.error(i18n.t('messages.error.rate_limit'), showErrorOptions);
          break;
        case 500:
          toast.error(i18n.t('messages.error.server_internal'), showErrorOptions);
          break;
        case 405:
          toast.info(i18n.t('messages.error.demo_no_server'));
          break;
        default:
          toast.error(
            i18n.t('messages.error.with_message', { message: error.message }),
            showErrorOptions
          );
      }
      return;
    }
    toast.error(
      i18n.t('messages.error.with_message', { message: error.message }),
      showErrorOptions
    );
  } else {
    toast.error(
      i18n.t('messages.error.with_message', { message: String(error) }),
      showErrorOptions
    );
  }
}

export function showWarning(message) {
  toast.warn(message, showWarningOptions);
}

export function showSuccess(message) {
  toast.success(message, showSuccessOptions);
}

export function showInfo(message) {
  toast.info(message, showInfoOptions);
}

export function showNotice(message, isHTML = false) {
  if (isHTML) {
    toast(<HTMLToastContent htmlContent={message} />, showNoticeOptions);
  } else {
    toast.info(message, showNoticeOptions);
  }
}

export function openPage(url) {
  window.open(url);
}

export function removeTrailingSlash(url) {
  if (url.endsWith('/')) {
    return url.slice(0, -1);
  } else {
    return url;
  }
}

export function timestamp2string(timestamp) {
  let date = new Date(timestamp * 1000);
  let year = date.getFullYear().toString();
  let month = (date.getMonth() + 1).toString();
  let day = date.getDate().toString();
  let hour = date.getHours().toString();
  let minute = date.getMinutes().toString();
  let second = date.getSeconds().toString();
  if (month.length === 1) {
    month = '0' + month;
  }
  if (day.length === 1) {
    day = '0' + day;
  }
  if (hour.length === 1) {
    hour = '0' + hour;
  }
  if (minute.length === 1) {
    minute = '0' + minute;
  }
  if (second.length === 1) {
    second = '0' + second;
  }
  return (
      year + '-' + month + '-' + day + ' ' + hour + ':' + minute + ':' + second
  );
}

export function downloadTextAsFile(text, filename) {
  let blob = new Blob([text], { type: 'text/plain;charset=utf-8' });
  let url = URL.createObjectURL(blob);
  let a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
}

export const verifyJSON = (str) => {
  try {
    JSON.parse(str);
  } catch (e) {
    return false;
  }
  return true;
};

export function shouldShowPrompt(id) {
  let prompt = localStorage.getItem(`prompt-${id}`);
  return !prompt;
}

export function setPromptShown(id) {
  localStorage.setItem(`prompt-${id}`, 'true');
}

let channelModels = undefined;
export async function loadChannelModels() {
  const res = await API.get('/api/models');
  const { success, data } = res.data;
  if (!success) {
    return;
  }
  channelModels = data;
  localStorage.setItem('channel_models', JSON.stringify(data));
}

export function getChannelModels(type) {
  if (channelModels !== undefined && type in channelModels) {
    return channelModels[type];
  }
  let models = localStorage.getItem('channel_models');
  if (!models) {
    return [];
  }
  channelModels = JSON.parse(models);
  if (type in channelModels) {
    return channelModels[type];
  }
  return [];
}

/** Login URL that returns the user to an in-app path after auth (open-redirect safe). */
export function loginWithNextReturnPath(returnPath) {
  if (!returnPath || typeof returnPath !== 'string' || !returnPath.startsWith('/')) {
    return '/login';
  }
  return `/login?next=${encodeURIComponent(returnPath)}`;
}

/** Decode ?next= for post-login navigation; only same-origin relative paths allowed. */
export function getSafeNextPath(nextParam) {
  if (!nextParam || typeof nextParam !== 'string') {
    return null;
  }
  try {
    const decoded = decodeURIComponent(nextParam);
    if (!decoded.startsWith('/') || decoded.startsWith('//')) {
      return null;
    }
    if (decoded.includes('://')) {
      return null;
    }
    return decoded;
  } catch {
    return null;
  }
}

/** User account group tier for Free / Plus / Pro gating (free → 0, plus → 1, pro → 2). */
export function userGroupTierRank(group) {
  const g = normalizeMemberGroup(group);
  if (g === 'pro') return 2;
  if (g === 'plus') return 1;
  return 0;
}

/** Required account tier to select a token model-scope preset. */
export function modelScopeTierRank(tier) {
  const t = normalizeScopeTier(tier);
  if (t === 'pro') return 2;
  if (t === 'plus') return 1;
  return 0;
}
